/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {toggleThemeStyle} from '../../theme/helpers';
import useThemeContext from '../../theme/useThemeContext';

const useStyles = makeStyles(() => ({
  root : {
    width : '100%'
  },
  groupContainer : {
    borderRadius    : toggleThemeStyle(0, 12),
    backgroundColor : toggleThemeStyle('transparent', dlsColor('grey13')),
    padding         : toggleThemeStyle('0px', '16px')
  }
}));

/**
 * Props for {@link FormGroupFields}.
 *
 * @property children
 * React content rendered inside the grouped area. This is typically a set of
 * `FormItem` components representing related fields.
 *
 * @property gridSpacing
 * Spacing passed to the internal `Grid container` that renders children.
 * Defaults to `4` to match `FormSection` spacing.
 *
 */
interface FormGroupFieldsProps {
  children: ReactNode;
  gridSpacing?: number;
  returnJustChildren?: boolean;
}

/**
 * FormGroupFields
 *
 * Lightweight layout wrapper used to visually group form controls with the
 * design-system background token `grey13`.
 *
 * Behavior:
 * - Renders as a single `Grid item` so parent form grid layout remains valid.
 * - Wraps all child fields inside one shared background container.
 * - Uses an internal `Grid container` so grouped fields retain normal grid
 *   alignment and spacing.
 *
 * This component intentionally keeps behavior minimal and focused on grouping
 * visuals while preserving grid compatibility.
 *
 * ```typescript
 * import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
 * import {FormItem} from '@ion/dls/src/components/forms/FormItem';
 * import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
 *
 * <FormGroupFields>
 *   <FormItem>
 *     <FormTextField fieldLabel='Make' />
 *   </FormItem>
 *   <FormItem>
 *     <FormTextField fieldLabel='Model' />
 *   </FormItem>
 * </FormGroupFields>
 * ```
 *
 * @param props Component props containing `children`.
 * @returns A single grouped field block with shared background styling.
 */
export const FormGroupFields = ({
  children,
  gridSpacing = 4,
  returnJustChildren = false
}: FormGroupFieldsProps) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const classes     = useStyles({isCurrentTheme});
  const items       = React.Children.toArray(children);

  if (!items.length || returnJustChildren) {
    return <>{children}</>;
  }

  return (
    <Grid item xs={12} className={classes.root}>
      <div className={classes.groupContainer}>
        <Grid container spacing={gridSpacing as any}>
          {items}
        </Grid>
      </div>
    </Grid>
  );
};

export default FormGroupFields;
